import {
  type ArtefactAdded,
  type ArtefactKind,
  type ArtefactKnowledgePreview,
  type ArtefactList,
  type ArtefactVersion,
  CSRF_HEADER,
  type FolderId,
} from "@esi/shared";
import { type QueryClient, useQuery } from "@tanstack/react-query";
import { api } from "../../api.ts";
import { ok } from "../../api-errors.ts";

/**
 * A conversation's artefacts as the viewer may read them (openspec change `artefacts-viewer`,
 * design §3), through the cookie client. The list is refetched when a turn ends or a card names
 * a newer version; a version's content is immutable, kept in memory only (never storage).
 */

/** Where a conversation lives: its project's routes, or the standalone ones. */
export interface ConversationScope {
  projectId: string | null;
  conversationId: string;
}

export const artefactKeys = {
  list: (conversationId: string) => ["artefacts", conversationId] as const,
  version: (artefactId: string, version: number) => ["artefact", artefactId, version] as const,
};

async function fetchList({ projectId, conversationId }: ConversationScope): Promise<ArtefactList> {
  return projectId
    ? ok(
        await api.api.projects[":projectId"].conversations[":conversationId"].artefacts.$get({
          param: { projectId, conversationId },
          // This app renders file artefacts (agent-turn D19).
          query: { files: "1" },
        }),
      )
    : ok(
        await api.api.conversations[":conversationId"].artefacts.$get({
          param: { conversationId },
          query: { files: "1" },
        }),
      );
}

async function fetchVersion(
  { projectId, conversationId }: ConversationScope,
  artefactId: string,
  version: number,
): Promise<ArtefactVersion> {
  const param = { conversationId, artefactId, version: String(version) };
  return projectId
    ? ok(
        await api.api.projects[":projectId"].conversations[":conversationId"].artefacts[
          ":artefactId"
        ].versions[":version"].$get({ param: { ...param, projectId } }),
      )
    : ok(
        await api.api.conversations[":conversationId"].artefacts[":artefactId"].versions[
          ":version"
        ].$get({ param }),
      );
}

/** The conversation's artefacts, each at the newest version the viewer may read. */
export function useArtefacts(scope: ConversationScope, enabled = true) {
  return useQuery({
    queryKey: artefactKeys.list(scope.conversationId),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: () => fetchList(scope),
  });
}

/** One version's content: immutable, so never refetched while cached. */
export function useArtefactVersion(
  scope: ConversationScope,
  artefactId: string | null,
  version: number | null,
) {
  return useQuery({
    queryKey: artefactKeys.version(artefactId ?? "", version ?? 0),
    enabled: artefactId !== null && version !== null,
    staleTime: Number.POSITIVE_INFINITY,
    // Content leaves memory a minute after its panel closes.
    gcTime: 60_000,
    retry: false,
    queryFn: () => fetchVersion(scope, artefactId as string, version as number),
  });
}

/**
 * A turn ended (or a card names a version the list lacks): the list is read again. A read
 * already in flight is joined, never cancelled (several cards may ask at once), and read once
 * more after it: it may have started before the write it is asked to show.
 */
export function refreshArtefacts(queryClient: QueryClient, conversationId: string) {
  const queryKey = artefactKeys.list(conversationId);
  const inFlight = queryClient.getQueryState(queryKey)?.fetchStatus === "fetching";
  const read = () =>
    queryClient.invalidateQueries({ queryKey, exact: true }, { cancelRefetch: false });
  const done = read();
  if (inFlight) {
    void done.then(read, read);
  }
}

/** A route of one version, under the project's routes or the standalone ones. */
function versionRoute(
  { projectId, conversationId }: ConversationScope,
  artefactId: string,
  version: number,
  leaf: "download" | "file" | "preview",
): string {
  const tail = `conversations/${encodeURIComponent(conversationId)}/artefacts/${encodeURIComponent(
    artefactId,
  )}/versions/${version}/${leaf}`;
  return projectId ? `/api/projects/${encodeURIComponent(projectId)}/${tail}` : `/api/${tail}`;
}

/**
 * The download route of a version: requested per click, never stored (design §6). A file
 * artefact's (agent-turn D19) redirects to a URL minted for that click.
 */
export function artefactDownloadHref(
  scope: ConversationScope,
  artefactId: string,
  version: number,
  kind?: ArtefactKind,
): string {
  return versionRoute(scope, artefactId, version, kind === "file" ? "file" : "download");
}

/** The inline preview of a file artefact image (PNG, JPEG), served through the API. */
export function artefactPreviewHref(
  scope: ConversationScope,
  artefactId: string,
  version: number,
): string {
  return versionRoute(scope, artefactId, version, "preview");
}

// ── Adding a version to knowledge (openspec change `artefacts-to-knowledge`) ──────────────

export const knowledgeKeys = {
  preview: (artefactId: string, version: number, target: string | null, folderId: string | null) =>
    ["artefact-knowledge", artefactId, version, target, folderId] as const,
  all: (artefactId: string) => ["artefact-knowledge", artefactId] as const,
};

/**
 * What adding the version to knowledge would create, for the viewer: who could read it, its
 * classification and origin, and whether it is already there (`state`). `target` is the picked
 * project of a standalone conversation.
 */
export async function fetchKnowledgePreview(
  { projectId, conversationId }: ConversationScope,
  artefactId: string,
  version: number,
  input: { target: string | null; folderId: string | null },
): Promise<ArtefactKnowledgePreview> {
  const param = { conversationId, artefactId, version: String(version) };
  const query = {
    ...(input.target ? { targetProjectId: input.target } : {}),
    ...(input.folderId ? { folderId: input.folderId } : {}),
  };
  return projectId
    ? ok(
        await api.api.projects[":projectId"].conversations[":conversationId"].artefacts[
          ":artefactId"
        ].versions[":version"].knowledge.$get({ param: { ...param, projectId }, query }),
      )
    : ok(
        await api.api.conversations[":conversationId"].artefacts[":artefactId"].versions[
          ":version"
        ].knowledge.$get({ param, query }),
      );
}

/** The viewer's preview of a version, read again whenever it is shown (access changes). */
export function useKnowledgePreview(
  scope: ConversationScope,
  artefactId: string,
  version: number | null,
  input: { target: string | null; folderId: string | null; enabled: boolean },
) {
  return useQuery({
    queryKey: knowledgeKeys.preview(artefactId, version ?? 0, input.target, input.folderId),
    enabled: input.enabled && version !== null,
    // A new folder pick keeps the form in place while its path is read.
    placeholderData: (previous) => previous,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () => fetchKnowledgePreview(scope, artefactId, version as number, input),
  });
}

/** Adds the version (`update`: as the document's next version); the server checks everything. */
export async function postKnowledge(
  { projectId, conversationId }: ConversationScope,
  artefactId: string,
  version: number,
  input: {
    update: boolean;
    title?: string;
    target: string | null;
    folderId: FolderId | null;
    csrfToken: string;
  },
): Promise<ArtefactAdded> {
  const param = { conversationId, artefactId, version: String(version) };
  const headers = { headers: { [CSRF_HEADER]: input.csrfToken } };
  const target = input.target ? { targetProjectId: input.target } : {};
  if (input.update) {
    return projectId
      ? ok(
          await api.api.projects[":projectId"].conversations[":conversationId"].artefacts[
            ":artefactId"
          ].versions[":version"].knowledge.update.$post(
            { param: { ...param, projectId }, json: target },
            headers,
          ),
        )
      : ok(
          await api.api.conversations[":conversationId"].artefacts[":artefactId"].versions[
            ":version"
          ].knowledge.update.$post({ param, json: target }, headers),
        );
  }
  const json = {
    ...target,
    ...(input.title ? { title: input.title } : {}),
    ...(input.folderId ? { folderId: input.folderId } : {}),
  };
  return projectId
    ? ok(
        await api.api.projects[":projectId"].conversations[":conversationId"].artefacts[
          ":artefactId"
        ].versions[":version"].knowledge.$post({ param: { ...param, projectId }, json }, headers),
      )
    : ok(
        await api.api.conversations[":conversationId"].artefacts[":artefactId"].versions[
          ":version"
        ].knowledge.$post({ param, json }, headers),
      );
}
